import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NG_VALIDATORS,
    Validator,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';

@Component({
    selector: 'sag-ui-checkbox',
    template: `
        <div class="flex items-center">
            <input
                type="checkbox"
                role="checkbox"
                [id]="id"
                [checked]="checked"
                [disabled]="disabled"
                [required]="required"
                [indeterminate]="intermediate"
                [attr.aria-checked]="checked"
                [attr.aria-disabled]="disabled"
                [attr.aria-labelledby]="ariaLabelledby || id + '_label'"
                [attr.aria-label]="ariaLabel"
                [attr.aria-describedby]="ariaDescribedby"
                (change)="onCheckboxChange($event)"
                (focus)="onFocus($event)"
                (blur)="onBlur($event)"
                (click)="click.emit($event)"
                (keydown)="keydown.emit($event)"
                (keyup)="keyup.emit($event)"
                (mouseenter)="mouseenter.emit($event)"
                (mouseleave)="mouseleave.emit($event)"
                sag-ui-checkbox
            />
            <label [for]="id" [id]="id + '_label'" sag-ui-checkbox-label class="pl-2 flex items-center">
                <ng-content></ng-content>
                <span *ngIf="required">*</span>
                <span
                    *ngIf="hint"
                    class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                    sag-ui-tooltip
                    [tooltip]="hint"
                ></span>
            </label>
        </div>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CheckboxComponent),
            multi: true
        }
    ]
})
export class CheckboxComponent implements ControlValueAccessor, Validator {
    @Input() id = this.randomId();
    @Input() checked = false;
    @Input() disabled = false;
    @Input() required = false;
    @Input() intermediate = false;
    @Input() hint?: string;

    // Accessibility inputs
    @Input() ariaLabel: string | null = null;
    @Input() ariaLabelledby: string | null = null;
    @Input() ariaDescribedby: string | null = null;

    // Outputs for events
    @Output() change = new EventEmitter<Event>();
    @Output() click = new EventEmitter<Event>();
    @Output() focus = new EventEmitter<Event>();
    @Output() blur = new EventEmitter<Event>();
    @Output() keydown = new EventEmitter<KeyboardEvent>();
    @Output() keyup = new EventEmitter<KeyboardEvent>();
    @Output() mouseenter = new EventEmitter<MouseEvent>();
    @Output() mouseleave = new EventEmitter<MouseEvent>();

    onChangeEvent = (_: any) => {};
    onTouched = () => {};

    writeValue(obj: any): void {
        this.checked = !!obj;
    }

    registerOnChange(fn: any): void {
        this.onChangeEvent = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onCheckboxChange(event: Event): void {
        event.stopPropagation();
        const input = event.target as HTMLInputElement;
        this.checked = input.checked;
        this.onChangeEvent(this.checked);
        this.change.emit(event);
    }

    onFocus(event: Event): void {
        this.focus.emit(event);
    }

    onBlur(event: Event): void {
        this.onTouched();
        this.blur.emit(event);
    }

    validate(_: AbstractControl): ValidationErrors | null {
        return this.required && !this.checked ? { required: true } : null;
    }

    private randomId(): string {
        return Math.random().toString(36).substring(2);
    }
}
